/// <amd-module name="Wit/Common/Controls/Boards/EditableField" />

import * as React from "react";
import { MakeOptional } from "VSS/Core/Util/Types";
import { IVssPageContext } from "VSS/Platform/Context";
import { css } from "VSS/Platform/Layout";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { IClientWorkItem } from "Wit/ClientWorkItemService/IClientWorkItem";
import { CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { FieldValue, WorkItemFieldControl } from "Wit/Common/WorkItemControls/WorkItemFieldControl";
import { IField, IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { FocusOnMount } from "./FocusOnMount";
import { getAllowedFieldValues } from "Wit/WorkItemModel/WorkItem/FieldUtils";

export type OnChangeType = (pageContext: IVssPageContext, item: IClientWorkItem, fieldReferenceName: string, newValue: FieldValue) => void;

interface IEditableFieldProps {
    allowFreeform?: boolean;
    autoSelect?: boolean;
    children: JSX.Element;
    className?: string;
    fieldReferenceName: string;
    inputType?: string;
    isIdentity?: boolean;
    item: IClientWorkItem;
    onChange: OnChangeType;
    onEditCompleted?: () => void;
    pageContext: IVssPageContext;
    startInEditingMode?: boolean;
}

/**
 * Show a field, and if that field is clicked, make it editable.
 *
 * Fields are only editable if the onChange prop is passed in.
 * @param props
 * @returns
 */
export function EditableField(props: MakeOptional<IEditableFieldProps, "onChange">) {
    if (!props.onChange) {
        return <div className={css(props.className, props.isIdentity && "is-identity")}>{props.children}</div>;
    } else {
        return <AlwaysEditableField {...(props as IEditableFieldProps)} />;
    }
}

/**
 * Like EditableField, but now onChange is a required prop.
 *
 * This lets EditableField be stateless in the read-only case.
 */
function AlwaysEditableField({
    allowFreeform,
    autoSelect,
    children,
    className,
    fieldReferenceName,
    inputType,
    isIdentity,
    item,
    onChange,
    onEditCompleted,
    pageContext,
    startInEditingMode
}: IEditableFieldProps) {
    const [editing, setEditing] = React.useState(false);
    const [fieldInfo, setFieldInfo] = React.useState<{ allowedValues?: string[]; field?: IField; fieldEntry?: IFieldEntry }>();

    const fillFieldInfo = async () => {
        if (!fieldInfo) {
            let iWorkItemPromise: Promise<IWorkItem> | undefined;

            if (item.id > 0 || item.iWorkItem.value) {
                // don't try to hydrate a new item unless it has already been initialized with an IWorkItem
                iWorkItemPromise = item.hydrate(pageContext);
            }

            const iWorkItem = await iWorkItemPromise;
            const field = iWorkItem?.getFieldByName(fieldReferenceName);

            if (!field) {
                console.error(`Could not load field for ${fieldReferenceName}`);
                return;
            }

            const allowedValues = iWorkItem && getAllowedFieldValues(field, iWorkItem);

            setFieldInfo({ allowedValues: allowedValues?.value, field });
        }
    };

    const beginEdit = () => {
        if (!editing && item.id > 0) {
            setEditing(true);
            fillFieldInfo();
            // go ahead and make sure the item is hydrated so that when the user saves it's already ready
            item.hydrate(pageContext);
        }
    };

    const clickHandler = React.useCallback(() => {
        beginEdit();
    }, [editing]);

    const editCompleted = () => {
        setEditing(false);
        onEditCompleted?.();
    };

    React.useEffect(() => {
        if (startInEditingMode) {
            beginEdit();
        }
    }, [startInEditingMode]);

    const projectId = item.getFieldValue<string>(CoreFieldRefNames.TeamProject) ?? "";
    return (
        <div className={css("editable-field", className, !editing && "not-editing", isIdentity && "is-identity")} onClick={clickHandler}>
            {!editing ? (
                children
            ) : !fieldInfo ? (
                <Spinner size={SpinnerSize.small} />
            ) : (
                <EditorComponent
                    allowFreeform={allowFreeform ?? true}
                    allowedValues={fieldInfo.allowedValues}
                    autoSelect={autoSelect ?? false}
                    field={fieldInfo.field}
                    fieldEntry={fieldInfo.fieldEntry}
                    inputType={inputType}
                    item={item}
                    onChange={onChange}
                    pageContext={pageContext}
                    projectId={projectId}
                    onEditCompleted={editCompleted}
                />
            )}
        </div>
    );
}

/**
 * Shows the actual editor for a field.
 */
function EditorComponent({
    allowFreeform,
    allowedValues,
    autoSelect,
    field,
    fieldEntry,
    inputType,
    item,
    onChange,
    pageContext,
    projectId,
    onEditCompleted
}: {
    allowFreeform: boolean;
    allowedValues: string[] | undefined;
    autoSelect: boolean | undefined;
    field: IField | undefined;
    fieldEntry: IFieldEntry | undefined;
    inputType: string | undefined;
    item: IClientWorkItem;
    onChange: OnChangeType;
    pageContext: IVssPageContext;
    projectId: string;
    onEditCompleted: () => void;
}) {
    const fieldName = field?.fieldDefinition.referenceName ?? fieldEntry?.referenceName;
    const fieldType = field?.fieldDefinition.type ?? fieldEntry?.type;
    if (!fieldName) {
        throw Error(`EditorComponent: Could not load field name.`);
    }
    const newValue = React.useRef<{ value: FieldValue; changed: boolean }>({ value: item.getFieldValue(fieldName), changed: false });

    const completeEditing = React.useCallback(() => {
        if (newValue.current.changed) {
            onChange(pageContext, item, fieldName, newValue.current.value);
        }

        onEditCompleted();
    }, [fieldName]);

    const keyHandler = React.useCallback(
        (e: React.KeyboardEvent) => {
            if (e.defaultPrevented && e.key !== "Escape") {
                return;
            }

            if (e.key === "Enter") {
                completeEditing();
            } else if (e.key === "Escape") {
                onEditCompleted();
            }
        },
        [completeEditing, fieldName]
    );

    const blurHandler = React.useCallback(
        (e: React.FocusEvent) => {
            if (
                e.defaultPrevented ||
                // Ignore blurring on DateTime fields as they will always blur while editing
                fieldType === FieldType.DateTime ||
                // Ignore blurring if the new focused element is within the container
                e.currentTarget.contains(e.relatedTarget as Node)
            ) {
                return;
            }

            completeEditing();
        },
        [fieldName, fieldType]
    );

    const changeHandler = React.useCallback(
        (value, changed) => {
            newValue.current = { value, changed };
            // Set edit false on new selection of datetime
            if (fieldType === FieldType.DateTime) {
                changed && onChange(pageContext, item, fieldName, value);
                onEditCompleted();
            }
        },
        [fieldName, fieldType, item]
    );

    return (
        <div className="editor-component" onKeyDown={keyHandler} onBlur={blurHandler}>
            <FocusOnMount>
                <WorkItemFieldControl
                    allowFreeform={allowFreeform}
                    allowedValues={allowedValues}
                    autoSelect={autoSelect ?? false}
                    datePickerExpandOnMount={true}
                    datePickerOnCollapse={completeEditing}
                    field={field}
                    initialValue={item.getFieldValue(fieldName)}
                    inputType={inputType}
                    onChange={changeHandler}
                />
            </FocusOnMount>
        </div>
    );
}
